<script lang="ts" setup>
import { ref, onMounted, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError } from '@/composables/useAxiosError'
import { usePagination } from '@/composables'
import { STATUS_CODES } from '@/constants'
import { DateTime } from 'luxon'
import { AdminHandleListItem, type PaginatedResponse } from '@/types'
import BaseDashboardPage from '@/components/base/BaseDashboardPage.vue'
import BaseSelect from '@/components/base/BaseSelect.vue'

defineOptions({ name: 'AdminHandlesList' })

const router = useRouter()

const loading = ref(true)
const handles = ref<AdminHandleListItem[]>([])
const typeFilter = ref<string>('all')
const verifiedFilter = ref<string>('all')
const searchQuery = ref('')

const pagination = usePagination({ initialItemsPerPage: 50 })

const typeOptions = [
  { value: 'all', title: 'Alle' },
  { value: 'person', title: 'Person' },
  { value: 'org', title: 'Organisation' },
  { value: 'role', title: 'Rolle' }
]

const verifiedOptions = [
  { value: 'all', title: 'Alle' },
  { value: 'true', title: 'Verifiziert' },
  { value: 'false', title: 'Nicht verifiziert' }
]

async function fetchHandles() {
  loading.value = true
  const { limit, offset } = pagination.buildQueryParams()

  let url = `/admin/handles?limit=${limit}&offset=${offset}`
  if (typeFilter.value !== 'all') {
    url += `&type=${typeFilter.value}`
  }
  if (verifiedFilter.value !== 'all') {
    url += `&verified=${verifiedFilter.value}`
  }

  try {
    const res = await useAxios<null, PaginatedResponse<AdminHandleListItem>>(
      'GET',
      url,
      true
    )

    if (res && res.status === STATUS_CODES.OK && res.data) {
      const validate = AdminHandleListItem.array().safeParse(res.data.data)
      if (validate.success) {
        handles.value = validate.data
      } else {
        handles.value = res.data.data as AdminHandleListItem[]
      }
      pagination.setTotal(res.data.total)
    }
  } catch (err) {
    handleAxiosError(err as Error, { logPrefix: 'AdminHandlesList' })
  } finally {
    loading.value = false
  }
}

watch(() => pagination.page.value, () => {
  fetchHandles()
})

watch([typeFilter, verifiedFilter], () => {
  pagination.goToPage(1)
  fetchHandles()
})

const filteredHandles = computed(() => {
  if (!searchQuery.value.trim()) {
    return handles.value
  }
  const query = searchQuery.value.toLowerCase()
  return handles.value.filter(h =>
    h.external_name?.toLowerCase().includes(query) ||
    h.account_email?.toLowerCase().includes(query)
  )
})

const showingFrom = computed(() => ((pagination.page.value - 1) * pagination.itemsPerPage.value) + 1)
const showingTo = computed(() => Math.min(showingFrom.value + handles.value.length - 1, pagination.total.value))

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '-'
  return DateTime.fromISO(dateString).toFormat('dd.MM.yyyy')
}

function getTypeLabel(type: string): string {
  const opt = typeOptions.find(t => t.value === type)
  return opt?.title || type
}

function getTypeClass(type: string): string {
  switch (type) {
    case 'person': return 'type-person'
    case 'org': return 'type-org'
    case 'role': return 'type-role'
    default: return ''
  }
}

function navigateToDetail(id: number) {
  router.push({ name: 'dashboardAdminHandleDetail', params: { id } })
}

onMounted(() => {
  fetchHandles()
})
</script>

<template>
  <BaseDashboardPage
    title="Alle Handles"
    subtitle="Alle Handles im System verwalten"
    icon="mdi-account-card"
    icon-color="purple"
  >
    <template #actions>
      <v-btn
        icon
        variant="text"
        :loading="loading"
        @click="fetchHandles"
      >
        <v-icon icon="mdi-refresh" />
      </v-btn>
    </template>

    <!-- Filters -->
    <div class="filters-bar">
      <v-text-field
        v-model="searchQuery"
        prepend-inner-icon="mdi-magnify"
        placeholder="Handle suchen..."
        variant="outlined"
        density="compact"
        hide-details
        class="search-input"
      />
      <BaseSelect
        v-model="typeFilter"
        :items="typeOptions"
        item-title="title"
        item-value="value"
        placeholder="Typ"
        class="type-select"
      />
      <BaseSelect
        v-model="verifiedFilter"
        :items="verifiedOptions"
        item-title="title"
        item-value="value"
        placeholder="Verifizierung"
        class="verified-select"
      />
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <v-progress-circular indeterminate size="48" color="primary" />
    </div>

    <!-- Empty State -->
    <div v-else-if="filteredHandles.length === 0" class="empty-state">
      <v-icon icon="mdi-account-off" size="64" />
      <h3>Keine Handles gefunden</h3>
      <p>Es wurden keine Handles mit den aktuellen Filtern gefunden.</p>
    </div>

    <!-- Handles Table -->
    <div v-else class="table-container">
      <table class="handles-table">
        <thead>
          <tr>
            <th>Handle</th>
            <th>Typ</th>
            <th>Account</th>
            <th>Verifizierung</th>
            <th>Erstellt</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="handle in filteredHandles"
            :key="handle.id"
            class="table-row"
            @click="navigateToDetail(handle.id)"
          >
            <td class="handle-cell">
              <span class="handle-name">{{ handle.external_name || '-' }}</span>
              <span class="handle-id">#{{ handle.id }}</span>
            </td>
            <td class="type-cell">
              <span class="type-badge" :class="getTypeClass(handle.type)">
                {{ getTypeLabel(handle.type) }}
              </span>
            </td>
            <td class="account-cell">
              <span class="account-email">{{ handle.account_email }}</span>
              <span class="account-id">#{{ handle.account_id }}</span>
            </td>
            <td class="verification-cell">
              <div class="verification-badges">
                <span class="verification-badge" :class="{ verified: handle.email_verified }">
                  <v-icon :icon="handle.email_verified ? 'mdi-email-check' : 'mdi-email-off'" size="16" />
                  E-Mail
                </span>
                <span class="verification-badge" :class="{ verified: handle.phone_verified }">
                  <v-icon :icon="handle.phone_verified ? 'mdi-phone-check' : 'mdi-phone-off'" size="16" />
                  Telefon
                </span>
              </div>
            </td>
            <td class="date-cell">{{ formatDate(handle.created) }}</td>
            <td class="action-cell">
              <v-btn icon variant="text" size="small">
                <v-icon icon="mdi-chevron-right" />
              </v-btn>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination Controls -->
    <div v-if="pagination.total.value > 0 && !loading" class="pagination-controls">
      <div class="pagination-info">
        Zeige {{ showingFrom }} bis {{ showingTo }} von {{ pagination.total.value }} Handles
      </div>
      <div class="pagination-buttons">
        <button
          class="pagination-btn"
          :disabled="pagination.page.value === 1"
          @click="pagination.goToPage(1)"
        >
          <v-icon icon="mdi-chevron-double-left" size="18" />
        </button>
        <button
          class="pagination-btn"
          :disabled="pagination.page.value === 1"
          @click="pagination.prevPage()"
        >
          <v-icon icon="mdi-chevron-left" size="18" />
        </button>
        <span class="page-indicator">
          {{ pagination.page.value }} / {{ pagination.totalPages.value }}
        </span>
        <button
          class="pagination-btn"
          :disabled="pagination.page.value === pagination.totalPages.value"
          @click="pagination.nextPage()"
        >
          <v-icon icon="mdi-chevron-right" size="18" />
        </button>
        <button
          class="pagination-btn"
          :disabled="pagination.page.value === pagination.totalPages.value"
          @click="pagination.goToPage(pagination.totalPages.value)"
        >
          <v-icon icon="mdi-chevron-double-right" size="18" />
        </button>
      </div>
    </div>
  </BaseDashboardPage>
</template>

<style lang="scss" scoped>
/* Filters */
.filters-bar {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.search-input {
  max-width: 280px;
  flex: 1;
}

.type-select,
.verified-select {
  max-width: 180px;
}

/* Loading/Empty States */
.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  text-align: center;
}

.empty-state {
  .v-icon {
    color: rgba(200, 218, 255, 0.3);
    margin-bottom: 16px;
  }

  h3 {
    font-size: 1.1rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.8);
    margin-bottom: 8px;
  }

  p {
    font-size: 0.9rem;
    color: rgba(200, 218, 255, 0.5);
    margin: 0;
  }
}

/* Table */
.table-container {
  background: rgba(15, 23, 48, 0.5);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
}

.handles-table {
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: 14px 16px;
    text-align: left;
  }

  th {
    font-size: 0.75rem;
    font-weight: 600;
    color: rgba(200, 218, 255, 0.5);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    background: rgba(0, 0, 0, 0.15);
    border-bottom: 1px solid rgba(120, 163, 255, 0.1);
  }
}

.table-row {
  border-bottom: 1px solid rgba(120, 163, 255, 0.05);
  transition: background 0.15s ease;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.02);
  }

  &:last-child {
    border-bottom: none;
  }
}

.handle-cell {
  .handle-name {
    display: block;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.9);
  }

  .handle-id {
    display: block;
    font-size: 0.75rem;
    color: rgba(200, 218, 255, 0.5);
    margin-top: 2px;
  }
}

.account-cell {
  .account-email {
    display: block;
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.8);
  }

  .account-id {
    display: block;
    font-size: 0.75rem;
    color: rgba(200, 218, 255, 0.5);
    margin-top: 2px;
  }
}

.date-cell {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.6);
  white-space: nowrap;
}

/* Type Badges */
.type-badge {
  display: inline-flex;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;

  &.type-person {
    background: rgba(33, 150, 243, 0.15);
    color: #64b5f6;
  }

  &.type-org {
    background: rgba(156, 39, 176, 0.15);
    color: #ce93d8;
  }

  &.type-role {
    background: rgba(255, 152, 0, 0.15);
    color: #ffb74d;
  }
}

/* Verification Badges */
.verification-cell {
  .verification-badges {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .verification-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 0.75rem;
    color: rgba(239, 83, 80, 0.8);

    .v-icon {
      color: rgba(239, 83, 80, 0.8);
    }

    &.verified {
      color: rgba(76, 175, 80, 0.9);

      .v-icon {
        color: rgba(76, 175, 80, 0.9);
      }
    }
  }
}

.action-cell {
  width: 48px;
  text-align: right;
}

/* Light Theme */
.v-theme--lightTheme {
  .table-container {
    background: rgba(255, 255, 255, 0.8);
    border-color: rgba(28, 110, 227, 0.1);
  }

  .handles-table th {
    background: rgba(0, 0, 0, 0.02);
    color: rgba(15, 23, 42, 0.5);
  }

  .handle-cell .handle-name {
    color: rgba(15, 23, 42, 0.9);
  }

  .handle-cell .handle-id {
    color: rgba(15, 23, 42, 0.5);
  }

  .account-cell .account-email {
    color: rgba(15, 23, 42, 0.8);
  }

  .account-cell .account-id {
    color: rgba(15, 23, 42, 0.5);
  }

  .date-cell {
    color: rgba(15, 23, 42, 0.6);
  }

  .empty-state {
    .v-icon {
      color: rgba(15, 23, 42, 0.2);
    }

    h3 {
      color: rgba(15, 23, 42, 0.8);
    }

    p {
      color: rgba(15, 23, 42, 0.5);
    }
  }
}

/* Pagination Controls */
.pagination-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  margin-top: 16px;
  background: rgba(15, 23, 48, 0.5);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
}

.pagination-info {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.6);
}

.pagination-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pagination-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.7);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: rgba(0, 176, 255, 0.1);
    border-color: rgba(0, 176, 255, 0.25);
    color: #00B0FF;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.page-indicator {
  font-size: 0.9rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  padding: 0 12px;
  min-width: 80px;
  text-align: center;
}

/* Mobile */
@media (max-width: 768px) {
  .admin-handles-list {
    padding: 16px;
  }

  .filters-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .search-input,
  .type-select,
  .verified-select {
    max-width: 100%;
  }

  .table-container {
    overflow-x: auto;
  }

  .handles-table {
    min-width: 700px;
  }

  .pagination-controls {
    flex-direction: column;
    gap: 12px;
  }
}
</style>
